// Tables & floor plan: dining-table CRUD with live occupancy, and the floor
// plan — the client's own layout document, stored and echoed as written —
// edited as JSON, with a starter layout drawn from the tables.

import { useCallback, useEffect, useState } from 'react';
import { LayoutGrid, Pencil, Trash2 } from 'lucide-react';
import { api, timeOf } from '../../lib/api';
import { useBase } from '../../scope';
import { Err } from './controls';
import { Check, errMsg, Head, intOrNull, SaveBtn, useAction } from './shared';
import type { DiningTable, DiningTableRequest, FloorPlan } from './types';

type TableDraft = { id: string | null; name: string; maxGuests: string; internalNotes: string; active: boolean; floorElementId: string | null };
const freshTable = (): TableDraft => ({ id: null, name: '', maxGuests: '', internalNotes: '', active: true, floorElementId: null });

export function Tables() {
  const base = useBase();
  const [tables, setTables] = useState<DiningTable[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const loadTables = useCallback(async () => {
    try {
      setTables((await api.get<DiningTable[]>(`${base}/tables`)).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })));
      setLoadErr(null);
    } catch (e) {
      setLoadErr(errMsg(e));
    }
  }, [base]);
  useEffect(() => { void loadTables(); }, [loadTables]);

  return (
    <>
      <Head title="Tables & floor plan" />
      <Err error={loadErr} />
      {tables === null && !loadErr && <div className="loading">Loading…</div>}
      {tables !== null && (
        <div className="set-cols">
          <TablesPanel base={base} tables={tables} reload={loadTables} />
          <FloorPlanPanel base={base} tables={tables} />
        </div>
      )}
    </>
  );
}

function TablesPanel(props: { base: string; tables: DiningTable[]; reload: () => Promise<void> }) {
  const { base, tables, reload } = props;
  const [draft, setDraft] = useState<TableDraft | null>(null);
  const act = useAction();

  const save = () => {
    if (!draft) return;
    const body: DiningTableRequest = {
      name: draft.name.trim(),
      maxGuests: intOrNull(draft.maxGuests),
      floorElementId: draft.floorElementId,
      internalNotes: draft.internalNotes.trim() || null,
      active: draft.active,
    };
    void act.run(async () => {
      if (draft.id) await api.put<DiningTable>(`${base}/tables/${draft.id}`, body);
      else await api.post<DiningTable>(`${base}/tables`, body);
      setDraft(null);
      await reload();
    });
  };
  const remove = (t: DiningTable) => {
    if (!confirm(`Delete table ${t.name}?`)) return;
    void act.run(async () => {
      await api.del(`${base}/tables/${t.tableId}`);
      setDraft((d) => (d?.id === t.tableId ? null : d));
      await reload();
    });
  };

  return (
    <div className="panel" data-section="tables">
      <h2>Dining tables</h2>
      {tables.length === 0 && <div className="loading">No tables yet.</div>}
      {tables.map((t) => (
        <div key={t.tableId} className="rowline" data-table-id={t.tableId} data-occupied={t.occupied}>
          <div className="grow">
            <b>{t.name}{t.active ? '' : ' (inactive)'}</b>
            <span className="sub">
              {t.maxGuests !== null ? `Seats ${t.maxGuests}` : 'No seat limit'}
              {t.internalNotes ? ` · ${t.internalNotes}` : ''}
            </span>
          </div>
          <span className={`chip ${t.occupied ? 'chip-in_progress' : ''}`}>{t.occupied ? 'occupied' : 'free'}</span>
          <button className="btn btn-ghost btn-sm" title={`Edit table ${t.name}`} aria-label={`Edit table ${t.name}`}
            onClick={() => setDraft({ id: t.tableId, name: t.name, maxGuests: t.maxGuests === null ? '' : String(t.maxGuests), internalNotes: t.internalNotes ?? '', active: t.active, floorElementId: t.floorElementId })}>
            <Pencil size={14} />
          </button>
          <button className="btn btn-ghost btn-sm" title={`Delete table ${t.name}`} aria-label={`Delete table ${t.name}`} disabled={act.busy} onClick={() => remove(t)}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {!draft && (
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-outline" onClick={() => setDraft(freshTable())}>New table</button>
        </div>
      )}
      {draft && (
        <div className="set-stack" style={{ marginTop: 14, gap: 12 }}>
          <div className="overline">{draft.id ? 'Edit table' : 'New table'}</div>
          <div className="frow">
            <div className="field">
              <label>Name</label>
              <input className="input" aria-label="Table name" placeholder="T1" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="field narrow">
              <label>Seats</label>
              <input className="input" type="number" min={1} aria-label="Seats" value={draft.maxGuests} onChange={(e) => setDraft({ ...draft, maxGuests: e.target.value })} />
            </div>
          </div>
          <div className="field">
            <label>Internal notes (optional)</label>
            <input className="input" aria-label="Table notes" placeholder="window booth" value={draft.internalNotes}
              onChange={(e) => setDraft({ ...draft, internalNotes: e.target.value })} />
          </div>
          <Check label="Active" checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} />
          <div style={{ display: 'flex', gap: 10 }}>
            <SaveBtn busy={act.busy} saved={false} onClick={save} disabled={!draft.name.trim()} label={draft.id ? 'Save table' : 'Create table'} />
            <button className="btn btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
      <Err error={act.error} />
    </div>
  );
}

function FloorPlanPanel(props: { base: string; tables: DiningTable[] }) {
  const { base, tables } = props;
  const [text, setText] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const act = useAction();

  const show = (fp: FloorPlan | null) => {
    setText(fp ? JSON.stringify(fp.layout, null, 2) : '');
    setUpdatedAt(fp?.updatedAt ?? null);
  };
  useEffect(() => {
    api.get<FloorPlan | null>(`${base}/floor-plan`).then(show, (e) => act.setError(errMsg(e)));
  }, [base, act.setError]);

  // A starter layout: every active table on a four-across grid.
  const layOut = () =>
    setText(JSON.stringify({
      elements: tables.filter((t) => t.active).map((t, i) => ({
        id: t.floorElementId ?? `el_${t.tableId}`, kind: 'table', tableId: t.tableId, x: 40 + (i % 4) * 120, y: 40 + Math.floor(i / 4) * 120,
      })),
    }, null, 2));

  const save = () => {
    let layout: unknown;
    try {
      layout = JSON.parse(text?.trim() || '{}');
    } catch {
      act.setError('That is not valid JSON — fix it before saving.');
      return;
    }
    void act.run(async () => show(await api.put<FloorPlan>(`${base}/floor-plan`, layout)));
  };

  return (
    <div className="panel" data-section="floor-plan">
      <h2>Floor plan</h2>
      <div className="hint" style={{ margin: '6px 0 10px' }}>
        The layout document the POS draws the room from, as JSON. A visual editor is a later slice.
        {updatedAt ? ` Last saved ${timeOf(updatedAt)}.` : ' Not saved yet.'}
      </div>
      {text === null ? <div className="loading">Loading…</div> : (
        <textarea className="input mono-area" style={{ minHeight: 320, width: '100%' }} aria-label="Floor plan layout" value={text}
          placeholder={'{ "elements": [] }'} onChange={(e) => setText(e.target.value)} />
      )}
      <Err error={act.error} />
      <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
        <SaveBtn busy={act.busy} saved={act.saved} onClick={save} label="Save floor plan" disabled={text === null} />
        <button className="btn btn-ghost btn-sm" disabled={tables.length === 0} onClick={layOut}><LayoutGrid size={14} /> Lay out the tables</button>
      </div>
    </div>
  );
}
